import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import { books } from "../data/books";
import { Atmosphere } from "../components/Atmosphere";

export default function Books() {
  // 1. Sprache aus dem Speicher holen & Event-Lauscher für die Navbar aufsetzen
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("project_lang") || "de";
  });

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(localStorage.getItem("project_lang") || "de");
    };
    window.addEventListener("languageChange", handleLangChange);
    return () => window.removeEventListener("languageChange", handleLangChange);
  }, []);

  return (
    <div className="pt-32 pb-24 px-6 relative" data-testid="books-page">
      <Atmosphere variant="minimal" />
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <p className="chinese text-[#C9A227]/70 text-2xl mb-3">六書</p>
          <h1 className="serif text-5xl lg:text-7xl text-[#E8DDC8] mb-4">
            {currentLang === "en" ? (
              <>The <span className="italic text-[#C9A227]">Six Volumes</span></>
            ) : (
              <>Die <span className="italic text-[#C9A227]">Sechs Bände</span></>
            )}
          </h1>
          <p className="serif italic text-[#B3B6B7] text-lg max-w-2xl mx-auto">
            {currentLang === "en"
              ? "A saga of magic and friendship."
              : "Eine Saga der Magie und Freundschaft."}
          </p>
        </motion.div>

        <div className="relative">
          {/* Vertikale Timeline-Linie (Mobil links ausgerichtet, Desktop zentriert) */}
          <div
            className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px -translate-x-1/2"
            style={{ background: "linear-gradient(to bottom, transparent, #8A6A1F, transparent)" }}
          />

          {books.map((b, i) => {
            const left = i % 2 === 0;
            const currentTitle = b.title[currentLang] || b.title["de"];
            const currentStatus = b.status[currentLang] || b.status["de"];

            return (
              <motion.div
                key={b.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.06 }}
                className={`relative flex flex-col lg:flex-row items-center gap-8 mb-24 ${
                  left ? "lg:flex-row" : "lg:flex-row-reverse"
                }`}
                data-testid={`book-${b.id}`}
              >
                {/* Timeline-Knotenpunkt (Node) */}
                <div className="absolute left-4 lg:left-1/2 -translate-x-1/2 top-8 lg:top-1/2 lg:-translate-y-1/2 z-20">
                  <div className="book-node" style={{ background: b.color, boxShadow: `0 0 14px 3px ${b.color}66` }} />
                </div>

                {/* LINKS / RECHTS: COVER */}
                <div className={`w-full lg:w-1/2 pl-10 lg:pl-0 ${left ? "lg:pr-14" : "lg:pl-14"}`}>
                  <div className={`max-w-[310px] aspect-[2/3] relative rounded overflow-hidden shadow-2xl border border-[#C9A227]/20 mx-auto ${left ? "lg:ml-auto lg:mr-0" : "lg:mr-auto lg:ml-0"}`}>
                    {b.coverUrl ? (
                      <img 
                        src={b.coverUrl} 
                        alt={currentTitle} 
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    ) : (
                      <div 
                        className="w-full h-full flex flex-col items-center justify-center p-4 text-center select-none"
                        style={{
                          background: `linear-gradient(135deg, #0e0e0e 0%, ${b.color}22 50%, #080808 100%)`
                        }}
                      >
                        <div className="absolute inset-2 border border-[#C9A227]/10" />
                        <span className="chinese text-6xl opacity-10 mb-2" style={{ color: b.color }}>書</span>
                        <p className="serif text-4xl mb-1 opacity-40" style={{ color: b.color }}>{b.number}</p>
                        <span className="text-[10px] uppercase tracking-widest text-[#7A7C7D]">
                          {currentStatus}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* GEGENSEITE: TEXT-CARD */}
                <div className={`w-full lg:w-1/2 pl-10 lg:pl-0 ${left ? "lg:pl-14" : "lg:pr-14"}`}>
                  <div className="card-imperial p-8">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span
                        className="serif text-5xl"
                        style={{ color: b.color }}
                      >
                        {b.number}
                      </span>
                      <span className="text-xs tracking-[0.3em] uppercase text-[#7A7C7D]">
                        {currentStatus}
                      </span>
                    </div>
                    <h3 className="serif text-3xl text-[#E8DDC8]">{currentTitle}</h3>
                    <p className="serif italic text-[#C9A227]/80 mt-1">
                      {b.subtitle[currentLang] || b.subtitle["de"]}
                    </p>
                    <p className="text-[#B3B6B7] mt-4 leading-relaxed text-sm">
                      {b.teaser[currentLang] || b.teaser["de"]}
                    </p>
                    
                    <div className="flex items-center gap-6 mt-6 text-xs text-[#7A7C7D] uppercase tracking-[0.2em] border-t border-[#C9A227]/10 pt-4">
                      <span>
                        {currentLang === "en" ? `${b.chapters} Chapters` : `${b.chapters} Kapitel`}
                      </span>
                      <span>·</span>
                      <span>
                        {currentLang === "en" ? `Volume ${b.number}` : `Band ${b.number}`}
                      </span>
                    </div>
                  </div>
                </div>

              </motion.div>
            );
          })}
        </div>

        {/* DICTIONARY PROMO BANNER (Ausserhalb der Schleife platziert) */}
        <div className="divider-gold mb-12" />
        <motion.div 
          whileHover={{ scale: 1.01 }}
          className="card-imperial p-8 mb-16 relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-6 border border-[#C9A227]/30 bg-gradient-to-r from-[#080808] to-[#14120c]"
        >
          <div className="absolute -right-10 -bottom-10 opacity-5 pointer-events-none select-none">
            <span className="chinese text-[12rem]">詞</span>
          </div>
          
          <div className="relative z-10">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C9A227]">
              {currentLang === "en" ? "Expand your knowledge" : "Erweitere dein Wissen"}
            </span>
            <h3 className="serif text-3xl text-[#E8DDC8] mt-1 mb-2">
              {currentLang === "en" ? "The Imperial Language Archive" : "Das kaiserliche Spracharchiv"}
            </h3>
            <p className="text-[#B3B6B7] text-sm max-w-xl leading-relaxed">
              {currentLang === "en"
                ? "Some secrets of Ruìhuá can only be decoded through its language. Visit our digital dictionary of Nirakh Veshan."
                : "Manche Geheimnisse von Ruìhuá lassen sich nur durch ihre Sprache entschlüsseln. Besuche unser digitales Wörterbuch von Nirakh Veshan."}
            </p>
          </div>
          
          <Link to="/woerterbuch" className="btn-imperial whitespace-nowrap relative z-10 flex items-center gap-2">
            {currentLang === "en" ? "Open Dictionary" : "Wörterbuch öffnen"} <BookOpen size={14} />
          </Link>
        </motion.div>

      </div>
    </div>
  );
}